// Refunds, credits, write-offs and card disputes on a customer's Money tab (gap #8). Money moves, so
// every one opens the same sheet: what will happen in words, what it needs (an amount, a reason),
// and a danger button that names the amount. The server decides and words every refusal; the
// permissions here only keep the tab from offering what it would refuse.

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { RpcError } from '@flowology/client';
import { dateText } from '@flowology/kernel';
import { creditWord } from './credit-words.ts';
import { client } from '../api.ts';
import { parseMoney } from '../books/model.ts';
import { newId } from '../shared/ids.ts';
import { MoneySheet } from '../shared/MoneySheet.tsx';
import type { CardCheck, Money } from './history-model.ts';
import {
  canResend,
  cardAnswerWords,
  cardCheckWords,
  methodWord,
  overWords,
  receiptWords,
  resentWords,
  voidable,
  voidRefusal,
  type CardAnswer,
  type moneyRights,
} from './money-model.ts';
import './money-actions.css';
import { useDesign } from '../blueprint/design.ts';
import { RowMore } from '../blueprint/RowMore.tsx';

const say = (e: unknown) =>
  e instanceof RpcError || e instanceof Error ? e.message : 'That didn’t go through. Try again.';
const cad = (cents: number) =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(cents / 100);
const dollars = (cents: number) => (cents / 100).toFixed(2);

export type Rights = ReturnType<typeof moneyRights>;
type Payment = NonNullable<Money['payments']>[number];
type Invoice = Money['invoices'][number];
/** What a payment can still give back (the server's refundableCents; all of it before that existed). */
const refundable = (p: Payment) => p.refundableCents ?? p.amountCents;

/**
 * An amount up to a ceiling, as typed ("135.45" or "$135.45"): `cents` once it fits, and `shown`, what
 * was typed, for the sheet's words (an amount over the ceiling is said as typed, never as $0.00).
 */
function useAmount(max: number) {
  const [text, setText] = useState(dollars(max));
  const cents = parseMoney(text);
  const typed = cents !== null && cents > 0 ? cents : null;
  const ok = typed !== null && typed <= max;
  return { text, setText, cents: ok ? typed : null, typed, shown: typed ?? 0 };
}

/** The amount, with why one over the ceiling can't go (QA-716): "That’s more than the $200.00 paid." */
function AmountField({
  amount,
  max,
  what,
}: {
  amount: ReturnType<typeof useAmount>;
  max: number;
  /** What the ceiling is, after its amount: "paid", "owed", "of credit left". */
  what: string;
}) {
  const over = overWords(amount.typed, max, what);
  return (
    <label>
      Amount
      <span className="muted small hint">Up to {cad(max)}.</span>
      <input
        className="field"
        inputMode="decimal"
        value={amount.text}
        aria-invalid={over ? true : undefined}
        onChange={(e) => amount.setText(e.target.value)}
      />
      {over && (
        <span className="error small" role="alert">
          {over}
        </span>
      )}
    </label>
  );
}

function ReasonField({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <label>
      Reason
      <input
        className="field"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </label>
  );
}

/**
 * The bank account a refund leaves from. A cheque (or a credit) starts at the company's first; an
 * e-transfer starts at the account it came into, which the server knows, and can be changed.
 */
function useBanks(kind: 'first' | 'cameInto' | null) {
  const [banks, setBanks] = useState<{ id: string; name: string }[] | null>(null);
  const [bankId, setBankId] = useState('');
  useEffect(() => {
    if (!kind) return;
    client
      .call<{ bankAccounts: { id: string; name: string }[] }>('bankAccounts.list', {})
      .then((r) => {
        setBanks(r.bankAccounts);
        if (kind === 'first') setBankId(r.bankAccounts[0]?.id ?? '');
      })
      .catch(() => setBanks([]));
  }, [kind]);
  return { kind, banks, bankId, setBankId };
}

function BankField({ banks }: { banks: ReturnType<typeof useBanks> }) {
  const list = banks.banks ?? [];
  if (list.length < (banks.kind === 'cameInto' ? 1 : 2)) return null;
  return (
    <label>
      From
      <select className="field" value={banks.bankId} onChange={(e) => banks.setBankId(e.target.value)}>
        {banks.kind === 'cameInto' && <option value="">The account it came into</option>}
        {list.map((b) => (
          <option key={b.id} value={b.id}>
            {b.name}
          </option>
        ))}
      </select>
    </label>
  );
}

// ── a payment: refund it, or record a card dispute ──

function RefundPayment({
  payment,
  onClose,
  onDone,
}: {
  payment: Payment;
  onClose: () => void;
  onDone: (m: string) => void;
}) {
  const amount = useAmount(refundable(payment));
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  // The refund's id is the processor's idempotency key: held, so a retry never refunds twice.
  const id = useRef(newId());
  const card = payment.method === 'card';
  const banks = useBanks(
    payment.method === 'cheque' ? 'first' : payment.method === 'etransfer' ? 'cameInto' : null,
  );
  const run = async () => {
    if (amount.cents === null) return;
    setBusy(true);
    setError(null);
    try {
      if (card) {
        const r = await client.call<{ state: string; message: string | null }>('payments.refundCard', {
          cardRefundId: id.current,
          paymentId: payment.id,
          amountCents: amount.cents,
          reason: reason.trim(),
        });
        if (r.state === 'approved') return onDone(`Refunded ${cad(amount.cents)} to the card.`);
        if (r.state === 'declined') {
          setError(r.message ?? 'The card processor declined the refund.');
          id.current = newId(); // a declined refund is over: another try is a new one
        } else
          setNote(
            r.message ??
              'The card processor hasn’t answered yet. The refund is recorded once it does; nothing is taken twice.',
          );
      } else {
        await client.call('payments.refundPayment', {
          refundId: id.current,
          paymentId: payment.id,
          amountCents: amount.cents,
          reason: reason.trim(),
          ...(banks.bankId ? { bankAccountId: banks.bankId } : {}),
        });
        return onDone(`Refunded ${cad(amount.cents)} by ${methodWord(payment.method)}.`);
      }
    } catch (e) {
      setError(say(e));
    }
    setBusy(false);
  };
  return (
    <MoneySheet
      title="Refund a payment"
      outcome={
        card ? (
          <>
            {cad(amount.shown)} goes back to the card this payment came from. The invoice stays paid and shows
            the refund; its revenue and tax go down by the same share.
          </>
        ) : (
          <>
            {cad(amount.shown)} is given back by {methodWord(payment.method)}. The invoice stays paid and
            shows the refund; its revenue and tax go down by the same share.
          </>
        )
      }
      confirm={`Refund ${cad(amount.shown)}`}
      ready={amount.cents !== null && !!reason.trim()}
      busy={busy}
      error={error}
      note={note && <p className="notice">{note}</p>}
      onConfirm={() => void run()}
      onClose={onClose}
    >
      <AmountField
        amount={amount}
        max={refundable(payment)}
        what={refundable(payment) < payment.amountCents ? 'left of this payment to give back' : 'paid'}
      />
      <BankField banks={banks} />
      <ReasonField value={reason} onChange={setReason} placeholder="Why they get it back" />
    </MoneySheet>
  );
}

function RecordDispute({
  payment,
  onClose,
  onDone,
}: {
  payment: Payment;
  onClose: () => void;
  onDone: (m: string) => void;
}) {
  const [stage, setStage] = useState<'opened' | 'won' | 'lost'>('opened');
  const amount = useAmount(payment.amountCents);
  const [fee, setFee] = useState('0.00');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const feeCents = parseMoney(fee);
  const OUTCOME = {
    opened: `${cad(amount.shown)} leaves the bank. The invoices it paid are open again, the customer goes on a collections hold, and the owner is told.`,
    won: 'The disputed money comes back, the invoices are paid again, and the collections hold comes off.',
    lost: 'The money stays with the cardholder. The invoices stay open to collect another way.',
  } as const;
  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      await client.call('payments.recordChargeback', {
        paymentId: payment.id,
        stage,
        ...(stage === 'opened' && amount.cents !== null ? { amountCents: amount.cents } : {}),
        feeCents: feeCents ?? 0,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      });
      onDone(stage === 'opened' ? 'The dispute is recorded.' : `The dispute is recorded as ${stage}.`);
    } catch (e) {
      setError(say(e));
      setBusy(false);
    }
  };
  return (
    <MoneySheet
      title="Record a card dispute"
      outcome={OUTCOME[stage]}
      confirm={stage === 'opened' ? `Record the dispute (${cad(amount.shown)})` : `Record it as ${stage}`}
      ready={(stage !== 'opened' || amount.cents !== null) && feeCents !== null && feeCents >= 0}
      busy={busy}
      error={error}
      onConfirm={() => void run()}
      onClose={onClose}
    >
      <label>
        What happened
        <select className="field" value={stage} onChange={(e) => setStage(e.target.value as typeof stage)}>
          <option value="opened">The cardholder disputed it</option>
          <option value="won">We won the dispute</option>
          <option value="lost">We lost the dispute</option>
        </select>
      </label>
      {stage === 'opened' && <AmountField amount={amount} max={payment.amountCents} what="paid" />}
      <label>
        Processor’s fee
        <input className="field" inputMode="decimal" value={fee} onChange={(e) => setFee(e.target.value)} />
      </label>
      <ReasonField value={reason} onChange={setReason} placeholder="Optional: what the cardholder said" />
    </MoneySheet>
  );
}

// ── a payment recorded in error: void it ──

interface PaymentOut {
  invoiceBalances: { invoiceId: string; number: string; balanceCents: number }[];
}

function VoidPayment({
  payment,
  onClose,
  onDone,
}: {
  payment: Payment;
  onClose: () => void;
  onDone: (m: string) => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Name the invoices that will owe again, when the payment says which they are.
  const [paid, setPaid] = useState<string[] | null>(null);
  useEffect(() => {
    client
      .call<PaymentOut>('payments.get', { id: payment.id })
      .then((r) => setPaid(r.invoiceBalances.map((b) => b.number)))
      .catch(() => setPaid(null));
  }, [payment.id]);
  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await client.call<PaymentOut>('payments.void', {
        paymentId: payment.id,
        reason: reason.trim(),
      });
      const owing = r.invoiceBalances.map((b) => `${b.number} owes ${cad(b.balanceCents)}`).join(', ');
      return onDone(`The ${cad(payment.amountCents)} payment is void.${owing ? ` ${owing} again.` : ''}`);
    } catch (e) {
      const why = e instanceof RpcError ? (e.detail as { reason?: string } | undefined)?.reason : undefined;
      setError(voidRefusal(say(e), why));
    }
    setBusy(false);
  };
  const settles =
    paid === null
      ? 'The invoices it paid owe that money again, any credit it left goes,'
      : paid.length === 0
        ? 'It paid no invoice, so the credit it left goes,'
        : `${paid.join(', ')} ${paid.length === 1 ? 'owes' : 'owe'} that money again, any credit it left goes,`;
  return (
    <MoneySheet
      title="Void a payment recorded in error"
      outcome={
        <>
          The {cad(payment.amountCents)} {methodWord(payment.method)} payment is kept, marked void with your
          reason. {settles} and the books post a reversal. To fix an amount, void it, then record the right
          payment.
        </>
      }
      confirm={`Void ${cad(payment.amountCents)}`}
      ready={!!reason.trim()}
      busy={busy}
      error={error}
      onConfirm={() => void run()}
      onClose={onClose}
    >
      <ReasonField value={reason} onChange={setReason} placeholder="What was recorded in error" />
    </MoneySheet>
  );
}

/** A payment's status on its row: nothing when recorded, "Void: <reason>" once voided. */
export function PaymentStatus({ payment }: { payment: Payment }) {
  if (payment.status === 'recorded') return null;
  if (payment.status === 'void')
    return <span className="small block">Void{payment.voidReason ? `: ${payment.voidReason}` : ''}</span>;
  return <> · {payment.status.replace(/_/g, ' ')}</>;
}

/**
 * A payment's receipt on its row (M5): how it went, or why none went, and Send again for a customer
 * it can go to. Send again keeps the automatic one's rules (the server's); its refusals are its words.
 */
/**
 * The receipt's words and its Send (again, now): the send drawn inline, or (Blueprint) left to the
 * row, which puts ReceiptSend under its More.
 */
export function PaymentReceiptLine({
  payment,
  channel,
  canSend,
  timeZone,
  onDone,
  inline = true,
}: {
  payment: Payment;
  /** Where a receipt sent now would go (customers.money), or null. */
  channel: 'email' | 'sms' | null | undefined;
  /** payments.record */
  canSend: boolean;
  timeZone: string;
  onDone: (m: string) => void;
  /** false: only the words; the row draws ReceiptSend where its other actions are */
  inline?: boolean;
}) {
  const r = payment.receipt ?? null;
  const offer = canSend && canResend(payment, channel);
  if (!r && !(offer && inline)) return null;
  return (
    <span className="receipt-line small">
      {r && <span className={r.failed ? 'error' : 'muted'}>{receiptWords(r, timeZone)}</span>}
      {offer && inline && (
        <ReceiptSend payment={payment} channel={channel} canSend={canSend} onDone={onDone} />
      )}
    </span>
  );
}

/** Send the payment's receipt (again, or now if it was held); nothing when there's none to send. */
export function ReceiptSend({
  payment,
  channel,
  canSend,
  onDone,
}: {
  payment: Payment;
  channel: 'email' | 'sms' | null | undefined;
  canSend: boolean;
  onDone: (m: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const r = payment.receipt ?? null;
  if (!(canSend && canResend(payment, channel))) return null;
  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      const out = await client.call<{ channel: 'email' | 'sms' }>(
        'payments.resendReceipt',
        { paymentId: payment.id },
        { idempotencyKey: newId() },
      );
      setBusy(false);
      onDone(resentWords(out.channel, r?.outcome === 'sent'));
    } catch (e) {
      setBusy(false);
      setError(say(e));
    }
  };
  return (
    <>
      <button className="btn quiet" disabled={busy} onClick={() => void send()}>
        {r?.outcome === 'sent' ? 'Send again' : r?.outcome === 'held' ? 'Send now' : 'Send receipt'}
      </button>
      {error && (
        <span className="error block" role="alert">
          {error}
        </span>
      )}
    </>
  );
}

export function PaymentActions({
  payment,
  rights,
  onDone,
  more,
}: {
  payment: Payment;
  rights: Rights;
  onDone: (m: string) => void;
  /** Blueprint: more of the row's actions (its receipt's Send) to put under its More */
  more?: ReactNode;
}) {
  const [open, setOpen] = useState<'refund' | 'dispute' | 'void' | null>(null);
  // Blueprint: one action at rest, Refund…; Dispute… and Void… under the row's More (DESIGN.md §3)
  const bp = useDesign() === 'blueprint';
  // A voided payment is kept for the record only: nothing more moves on it.
  const moves = !(payment.status === 'void' || payment.amountCents <= 0 || !(rights.refund || rights.void));
  if (!moves) return bp && more ? <RowMore about="this payment">{more}</RowMore> : null;
  const left = refundable(payment);
  const done = (m: string) => {
    setOpen(null);
    onDone(m);
  };
  const dispute = rights.refund && payment.method === 'card' && (
    <button className="btn quiet" onClick={() => setOpen('dispute')}>
      Dispute…
    </button>
  );
  const voiding = rights.void && voidable(payment) && (
    <button className="btn quiet" onClick={() => setOpen('void')}>
      Void…
    </button>
  );
  return (
    <span className="row money-acts">
      {rights.refund &&
        (left > 0 ? (
          <button className="btn quiet" onClick={() => setOpen('refund')}>
            Refund…
          </button>
        ) : (
          <span className="muted small">Nothing left to refund</span>
        ))}
      {bp ? (
        (dispute || voiding || more) && (
          <RowMore about="this payment">
            {dispute}
            {voiding}
            {more}
          </RowMore>
        )
      ) : (
        <>
          {dispute}
          {voiding}
        </>
      )}
      {open === 'void' && <VoidPayment payment={payment} onClose={() => setOpen(null)} onDone={done} />}
      {open === 'refund' && (
        <RefundPayment
          payment={payment}
          onClose={() => setOpen(null)}
          onDone={(m) => {
            setOpen(null);
            onDone(m);
          }}
        />
      )}
      {open === 'dispute' && (
        <RecordDispute
          payment={payment}
          onClose={() => setOpen(null)}
          onDone={(m) => {
            setOpen(null);
            onDone(m);
          }}
        />
      )}
    </span>
  );
}

// ── a card charge still being checked: check it again, or cancel it ──

/**
 * While a card charge on an invoice is unknown nothing else is taken on it (QA-307), so the office
 * sees the charge where it sees the invoice, and asks the processor again or cancels the charge.
 */
export function CardBeingChecked({
  check,
  timeZone,
  canAct,
  onDone,
}: {
  check: CardCheck;
  /** The company's, to say when the charge was started (when that is known). */
  timeZone?: string;
  /** payments.record: the office that takes payments. */
  canAct: boolean;
  onDone: (m: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const ask = async (asked: 'check' | 'cancel') => {
    setBusy(true);
    setError(null);
    try {
      const r = await client.call<CardAnswer>(
        asked === 'check' ? 'payments.checkCard' : 'payments.cancelUnknown',
        { checkoutId: check.checkoutId },
      );
      setBusy(false);
      setCancelling(false);
      onDone(cardAnswerWords(r, asked));
    } catch (e) {
      setBusy(false);
      setError(say(e));
    }
  };
  return (
    <div className="card-check">
      <span className="small block">{cardCheckWords(check, timeZone)}</span>
      {canAct && (
        <span className="row money-acts">
          <button className="btn quiet" disabled={busy} onClick={() => void ask('check')}>
            Check again
          </button>
          <button className="btn quiet" disabled={busy} onClick={() => setCancelling(true)}>
            Cancel this charge…
          </button>
        </span>
      )}
      {error && !cancelling && (
        <span className="error small block" role="alert">
          {error}
        </span>
      )}
      {cancelling && (
        <MoneySheet
          title="Cancel this card charge"
          outcome={
            <>
              Asks the card processor to cancel the charge
              {check.amountCents !== undefined ? ` of ${cad(check.amountCents)}` : ''}, so the customer can
              pay another way. If it already went through, it is recorded instead. Until the processor
              confirms the cancel, the invoice still takes no other payment.
            </>
          }
          confirm="Cancel this charge"
          ready
          busy={busy}
          error={error}
          onConfirm={() => void ask('cancel')}
          onClose={() => {
            setCancelling(false);
            setError(null);
          }}
        />
      )}
    </div>
  );
}

// ── an invoice: write off what's owed, or keep a draft's payment as credit ──

/** Write off what's owed on an invoice (the owner's): on the Money tab and the invoice sheet. */
export function WriteOff({
  invoice,
  onClose,
  onDone,
}: {
  invoice: Pick<Invoice, 'id' | 'number' | 'balanceCents'>;
  onClose: () => void;
  onDone: (m: string) => void;
}) {
  const amount = useAmount(invoice.balanceCents);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async () => {
    if (amount.cents === null) return;
    setBusy(true);
    setError(null);
    try {
      await client.call('invoices.writeOff', {
        invoiceId: invoice.id,
        ...(amount.cents !== invoice.balanceCents ? { amountCents: amount.cents } : {}),
        reason: reason.trim(),
      });
      onDone(`${cad(amount.cents)} on ${invoice.number} is written off.`);
    } catch (e) {
      setError(say(e));
      setBusy(false);
    }
  };
  return (
    <MoneySheet
      title={`Write off ${invoice.number}`}
      outcome={
        <>
          {cad(amount.shown)} of what’s owed on {invoice.number} becomes a bad debt and is no longer
          collected. The tax inside it comes back on the next return.
        </>
      }
      confirm={`Write off ${cad(amount.shown)}`}
      ready={amount.cents !== null && !!reason.trim()}
      busy={busy}
      error={error}
      onConfirm={() => void run()}
      onClose={onClose}
    >
      <AmountField amount={amount} max={invoice.balanceCents} what="owed" />
      <ReasonField value={reason} onChange={setReason} placeholder="Why it won’t be collected" />
    </MoneySheet>
  );
}

export function KeepAsCredit({
  invoice,
  onClose,
  onDone,
}: {
  invoice: { id: string; number: string; paidCents: number };
  onClose: () => void;
  onDone: (m: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await client.call<{ creditedCents: number }>('payments.keepAsCredit', {
        invoiceId: invoice.id,
      });
      onDone(`${cad(r.creditedCents)} is the customer’s credit now.`);
    } catch (e) {
      setError(say(e));
      setBusy(false);
    }
  };
  return (
    <MoneySheet
      title="Keep the payment as credit"
      outcome={
        <>
          The {cad(invoice.paidCents)} paid on draft {invoice.number} moves to the customer’s credit, so the
          job can be called off. The credit can then be refunded or used on another invoice.
        </>
      }
      confirm={`Keep ${cad(invoice.paidCents)} as credit`}
      ready
      busy={busy}
      error={error}
      onConfirm={() => void run()}
      onClose={onClose}
    />
  );
}

interface Refunds {
  refundedCents: number;
  refunds: { refundId: string; amountCents: number; method: string; refundedOn: string; reason: string }[];
}

export function InvoiceMoneyActions({
  invoice,
  rights,
  version,
  timeZone,
  onDone,
  onRecord,
}: {
  invoice: Invoice;
  rights: Rights;
  /** The company's, for when a card charge still being checked was started. */
  timeZone: string;
  /** Bumped by every money move on the tab, so a new refund shows here. */
  version: number;
  onDone: (m: string) => void;
  /** "Record payment…": the tab opens the shared form, this invoice first. */
  onRecord?: () => void;
}) {
  const [open, setOpen] = useState<'writeOff' | null>(null);
  const [refunds, setRefunds] = useState<Refunds | null>(null);
  // Blueprint: one action at rest, Record payment…; Write off… under the row's More (DESIGN.md §3)
  const bp = useDesign() === 'blueprint';
  const paid = invoice.totalCents - invoice.balanceCents;
  // Only an invoice something was paid on can carry a refund.
  useEffect(() => {
    if (paid <= 0 || invoice.status === 'draft') return;
    client
      .call<Refunds>('invoices.refunds', { invoiceId: invoice.id })
      .then(setRefunds)
      .catch(() => undefined);
  }, [invoice.id, paid, invoice.status, version]);
  const day = (d: string) => dateText(d, 'UTC', { year: false, now: new Date() });
  const canWriteOff =
    rights.writeOff && invoice.balanceCents > 0 && !['draft', 'void'].includes(invoice.status);
  const held = invoice.cardBeingChecked ?? null;
  // While a card charge on it is being checked, the server takes nothing else: the charge is settled first.
  const canRecord =
    rights.record && !held && invoice.balanceCents > 0 && !['draft', 'void'].includes(invoice.status);
  // A paid draft is kept as credit at Review (review/KeepCredit.tsx): customers.money lists no drafts
  // (QA-384), so the Money tab never offers it.
  return (
    <>
      {(invoice.writtenOffCents ?? 0) > 0 && (
        <span className="muted small block">Written off {cad(invoice.writtenOffCents ?? 0)}</span>
      )}
      {refunds && refunds.refundedCents > 0 && (
        <span className="muted small block">
          Refunded {cad(refunds.refundedCents)}
          {refunds.refunds[0] &&
            ` on ${day(refunds.refunds[0].refundedOn)} by ${methodWord(refunds.refunds[0].method)}`}
          {refunds.refunds.length > 1 ? ` and ${refunds.refunds.length - 1} more` : ''}
        </span>
      )}
      {held && <CardBeingChecked check={held} timeZone={timeZone} canAct={rights.record} onDone={onDone} />}
      {(canWriteOff || (onRecord && canRecord)) && (
        <span className="row money-acts">
          {onRecord && canRecord && (
            <button className="btn quiet" onClick={onRecord}>
              Record payment…
            </button>
          )}
          {canWriteOff &&
            (bp ? (
              <RowMore about={`invoice ${invoice.number}`}>
                <button className="btn quiet" onClick={() => setOpen('writeOff')}>
                  Write off…
                </button>
              </RowMore>
            ) : (
              <button className="btn quiet" onClick={() => setOpen('writeOff')}>
                Write off…
              </button>
            ))}
        </span>
      )}
      {open === 'writeOff' && (
        <WriteOff
          invoice={invoice}
          onClose={() => setOpen(null)}
          onDone={(m) => {
            setOpen(null);
            onDone(m);
          }}
        />
      )}
    </>
  );
}

// ── credit on account: use it on an invoice, or give it back ──

interface Credit {
  id: string;
  origin: string;
  amountCents: number;
  remainingCents: number;
  createdAt: string;
  /** The payment that left it, and how it was paid: a card's goes back to the card (payments.refundCard). */
  paymentId?: string | null;
  paymentMethod?: string | null;
  /** A membership's cancel refunded it, by credit note or from deferred revenue (QA-646). */
  membershipCancel?: boolean;
}

function ApplyCredit({
  credit,
  invoices,
  onClose,
  onDone,
}: {
  credit: Credit;
  invoices: Invoice[];
  onClose: () => void;
  onDone: (m: string) => void;
}) {
  const open = invoices.filter((i) => i.balanceCents > 0 && i.status !== 'void');
  const [invoiceId, setInvoiceId] = useState(open[0]?.id ?? '');
  const target = open.find((i) => i.id === invoiceId);
  const max = Math.min(credit.remainingCents, target?.balanceCents ?? credit.remainingCents);
  const amount = useAmount(max);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Another invoice picked: the amount starts again at the most that can go on it.
  const { setText } = amount;
  useEffect(() => setText(dollars(max)), [max, setText]);
  const run = async () => {
    if (amount.cents === null || !target) return;
    setBusy(true);
    setError(null);
    try {
      await client.call('credits.apply', {
        creditId: credit.id,
        invoiceId: target.id,
        amountCents: amount.cents,
      });
      onDone(`${cad(amount.cents)} of credit went on ${target.number}.`);
    } catch (e) {
      setError(say(e));
      setBusy(false);
    }
  };
  return (
    <MoneySheet
      title="Use credit on an invoice"
      outcome={
        target ? (
          <>
            {cad(amount.shown)} of the customer’s credit pays {target.number}.{' '}
            {cad(Math.max(0, credit.remainingCents - amount.shown))} credit stays on account.
          </>
        ) : (
          'This customer has no open invoice to use it on.'
        )
      }
      confirm={`Use ${cad(amount.shown)}`}
      ready={!!target && amount.cents !== null}
      busy={busy}
      error={error}
      onConfirm={() => void run()}
      onClose={onClose}
    >
      {open.length > 0 && (
        <label>
          Invoice
          <select className="field" value={invoiceId} onChange={(e) => setInvoiceId(e.target.value)}>
            {open.map((i) => (
              <option key={i.id} value={i.id}>
                {i.number} · {cad(i.balanceCents)} owing
              </option>
            ))}
          </select>
        </label>
      )}
      {target && <AmountField amount={amount} max={max} what="that can go on it" />}
    </MoneySheet>
  );
}

function RefundCredit({
  credit,
  onClose,
  onDone,
}: {
  credit: Credit;
  onClose: () => void;
  onDone: (m: string) => void;
}) {
  const amount = useAmount(credit.remainingCents);
  // a card payment's credit (a deposit by card) goes back to that card, through the processor
  const toCard = credit.paymentMethod === 'card' && !!credit.paymentId;
  const [method, setMethod] = useState<'etransfer' | 'cheque' | 'cash' | 'card'>(
    toCard ? 'card' : 'etransfer',
  );
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const id = useRef(newId());
  const banks = useBanks('first');
  const run = async () => {
    if (amount.cents === null) return;
    setBusy(true);
    setError(null);
    try {
      if (toCard && method === 'card') {
        const r = await client.call<{ state: string; message: string | null }>('payments.refundCard', {
          cardRefundId: id.current,
          paymentId: credit.paymentId,
          amountCents: amount.cents,
          reason: reason.trim(),
        });
        if (r.state !== 'approved') {
          setError(r.message ?? 'The card processor didn’t give it back. Try again later.');
          setBusy(false);
          return;
        }
        return onDone(`Refunded ${cad(amount.cents)} to the card.`);
      }
      await client.call('payments.refund', {
        refundId: id.current,
        creditId: credit.id,
        amountCents: amount.cents,
        method,
        reason: reason.trim(),
        ...(banks.bankId ? { bankAccountId: banks.bankId } : {}),
      });
      onDone(`Refunded ${cad(amount.cents)} of credit by ${methodWord(method)}.`);
    } catch (e) {
      setError(say(e));
      setBusy(false);
    }
  };
  return (
    <MoneySheet
      title="Refund credit"
      outcome={
        <>
          {cad(amount.shown)} of the customer’s credit is paid back to them by {methodWord(method)}.{' '}
          {cad(Math.max(0, credit.remainingCents - amount.shown))} stays on account.
        </>
      }
      confirm={`Refund ${cad(amount.shown)}`}
      ready={amount.cents !== null && !!reason.trim()}
      busy={busy}
      error={error}
      onConfirm={() => void run()}
      onClose={onClose}
    >
      <AmountField amount={amount} max={credit.remainingCents} what="of credit left" />
      <label>
        How it goes back
        <select className="field" value={method} onChange={(e) => setMethod(e.target.value as typeof method)}>
          <option value="etransfer">E-transfer</option>
          <option value="cheque">Cheque</option>
          <option value="cash">Cash</option>
          <option value="card">
            {toCard ? 'Back to the card it came from' : 'Card (refunded at the terminal first)'}
          </option>
        </select>
      </label>
      <BankField banks={banks} />
      <ReasonField value={reason} onChange={setReason} placeholder="Why they get it back" />
    </MoneySheet>
  );
}

export function Credits({
  customerId,
  invoices,
  rights,
  version,
  onDone,
}: {
  customerId: string;
  invoices: Invoice[];
  rights: Rights;
  version: number;
  onDone: (m: string) => void;
}) {
  const [credits, setCredits] = useState<Credit[] | null>(null);
  const [open, setOpen] = useState<{ kind: 'apply' | 'refund'; credit: Credit } | null>(null);
  useEffect(() => {
    client
      .call<{ credits: Credit[] }>('credits.list', { customerId })
      .then((r) => setCredits(r.credits))
      .catch(() => setCredits([]));
  }, [customerId, version]);
  if (!credits || credits.length === 0) return null;
  const done = (m: string) => {
    setOpen(null);
    onDone(m);
  };
  return (
    <>
      <h3>Credit on account</h3>
      <ul className="rows">
        {credits.map((c) => (
          <li key={c.id} className="mat money-row">
            <span>
              {creditWord(c)}
              <span className="muted small block">
                {cad(c.remainingCents)} left of {cad(c.amountCents)}
              </span>
            </span>
            <span className="row money-acts">
              <b>{cad(c.remainingCents)}</b>
              {rights.edit && (
                <button className="btn quiet" onClick={() => setOpen({ kind: 'apply', credit: c })}>
                  Use on an invoice…
                </button>
              )}
              {rights.refund && (
                <button className="btn quiet" onClick={() => setOpen({ kind: 'refund', credit: c })}>
                  Refund…
                </button>
              )}
            </span>
          </li>
        ))}
      </ul>
      {open?.kind === 'apply' && (
        <ApplyCredit credit={open.credit} invoices={invoices} onClose={() => setOpen(null)} onDone={done} />
      )}
      {open?.kind === 'refund' && (
        <RefundCredit credit={open.credit} onClose={() => setOpen(null)} onDone={done} />
      )}
    </>
  );
}
